import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Inbox, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export function LoadingState({
  label = "Loading content",
}: {
  label?: string;
}) {
  return (
    <Stack aria-live="polite" aria-label={label} spacing={2}>
      <Skeleton variant="rounded" height={100} />
      <Skeleton variant="rounded" height={260} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} />
        <Typography color="text.secondary">{label}…</Typography>
      </Box>
    </Stack>
  );
}

export function ErrorState({
  message,
  retry,
}: {
  message: string;
  retry?: () => void;
}) {
  return (
    <Alert
      severity="error"
      action={
        retry ? (
          <Button color="inherit" onClick={retry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      {message}
    </Alert>
  );
}

export function EmptyState({
  title,
  description,
  icon: Icon = Inbox,
  action,
  compact = false,
}: {
  title: string;
  description: string;
  icon?: LucideIcon;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <Paper
      elevation={0}
      role="status"
      sx={{
        py: compact ? 3 : { xs: 4, sm: 5 },
        px: { xs: 2, sm: 3 },
        textAlign: "center",
        width: "100%",
        boxSizing: "border-box",
        border: 0,
        boxShadow: "none",
        bgcolor: "transparent",
      }}
    >
      <Box
        sx={{
          color: "text.secondary",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Icon size={compact ? 28 : 40} strokeWidth={1.5} aria-hidden="true" />
      </Box>
      <Typography
        component="h2"
        sx={{ mt: 2, fontSize: compact ? 14 : 17, fontWeight: 750 }}
      >
        {title}
      </Typography>
      <Typography
        color="text.secondary"
        sx={{
          mt: 0.75,
          fontSize: 13,
          lineHeight: 1.7,
          maxWidth: 420,
          mx: "auto",
        }}
      >
        {description}
      </Typography>
      {action && (
        <Box
          sx={{
            mt: 2.5,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {action}
        </Box>
      )}
    </Paper>
  );
}
